import SignOutButton from "@/components/shared/SignOutButton";
import { Link, usePage } from "@inertiajs/react";
import { LayoutDashboard, HelpCircle, Users as UsersIcon, Building2, School as SchoolIcon } from "lucide-react";

/**
 * Layout for the /admin area. Not the marketing chrome — this is an internal
 * "ops" shell: warm cream sidebar, minimal nav, grounded typography. Still
 * uses the PagePledge palette so it doesn't feel like a different app.
 *
 * Slots:
 *   children: the page content
 *   title:    shown in the page header (optional)
 *   eyebrow:  small uppercase label above the title (optional)
 *   actions:  right-aligned action buttons in the page header (optional)
 */
export default function AdminLayout({ children, title, eyebrow, actions }) {
  const { auth } = usePage().props;

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-background">

      {/* ── Sidebar ──────────────────────────────────────── */}
      <aside className="md:w-60 md:min-h-screen border-b md:border-b-0 md:border-r border-border bg-card/60">
        <div className="p-5 border-b border-border">
          <Link href="/admin" className="inline-flex items-baseline gap-1 font-display text-lg font-semibold text-foreground hover:text-primary transition-colors">
            PagePledge
            <span className="text-primary">.</span>
          </Link>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
            Platform admin
          </p>
        </div>

        <nav className="p-3 space-y-1">
          <AdminNavLink href="/admin" icon={<LayoutDashboard className="h-4 w-4" />}>
            Dashboard
          </AdminNavLink>
          <AdminNavLink href="/admin/schools" icon={<SchoolIcon className="h-4 w-4" />}>
            Schools
          </AdminNavLink>
          <AdminNavLink href="/admin/school_prospects" icon={<Building2 className="h-4 w-4" />}>
            School prospects
          </AdminNavLink>
          <AdminNavLink href="/admin/users" icon={<UsersIcon className="h-4 w-4" />}>
            Users
          </AdminNavLink>
          <AdminNavLink href="/help" icon={<HelpCircle className="h-4 w-4" />}>
            Help centre
          </AdminNavLink>
        </nav>

        <div className="p-3 mt-auto border-t border-border md:absolute md:bottom-0 md:w-60">
          {auth?.user && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground px-2">
                <span className="font-medium text-foreground">{auth.user.display_name}</span>
              </p>
              <SignOutButton variant="full" />
            </div>
          )}
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────── */}
      <main className="flex-1 min-w-0">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          {(title || actions) && (
            <header className="mb-10 flex flex-wrap items-start justify-between gap-4">
              <div>
                {eyebrow && (
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                    {eyebrow}
                  </p>
                )}
                {title && (
                  <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
                    {title}
                  </h1>
                )}
              </div>
              {actions && <div className="flex gap-2">{actions}</div>}
            </header>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}

function AdminNavLink({ href, icon, children }) {
  const { url } = usePage();
  // The Dashboard link lives at /admin (the root of this section), so a
  // naive `startsWith(href + "/")` would match every /admin/* route and
  // highlight Dashboard everywhere. Treat `/admin` as exact-only and let
  // sub-section links use the prefix match.
  const path = url.split("?")[0];
  const isSectionRoot = href === "/admin";
  const active = isSectionRoot
    ? path === href || path === href + "/"
    : path === href || path.startsWith(href + "/");
  return (
    <Link
      href={href}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
        active
          ? "bg-primary/10 text-primary"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
      }`}
    >
      {icon}
      {children}
    </Link>
  );
}
